import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Camera, RefreshCw, X, Upload } from 'lucide-react';
import { BUTTON_STYLES, CARD_STYLES, PHOTO_CAPTURE } from '../../utils/constants';

const WebcamCapture = ({
  onPhotoCapture,
  onPhotoRemove,
  capturedPhoto,
  loading = false,
  disabled = false,
  className = ''
}) => {
  const [isWebcamActive, setIsWebcamActive] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [stream, setStream] = useState(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const stopWebcam = useCallback(() => {
    try {
      const video = videoRef.current;
      if (video) {
        try {
          video.pause();
        } catch (_) {}
        video.srcObject = null;
      }
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
    } catch (_) {}
    setStream(null);
    setIsWebcamActive(false);
    setIsVideoReady(false);
    setStarting(false);
  }, [stream]);

  const startWebcam = useCallback(async () => {
    if (starting || isWebcamActive) return;
    setError(null);
    setStarting(true);
    setIsVideoReady(false);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw Object.assign(new Error('Your browser does not support camera access.'), { name: 'UnsupportedBrowser' });
      }
      // Only block when not a secure context AND not a loopback host
      const isLoopbackHost = ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
      if (!window.isSecureContext && !isLoopbackHost) {
        throw Object.assign(new Error('Camera access requires a secure connection (HTTPS).'), { name: 'SecurityError' });
      }
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: PHOTO_CAPTURE.videoConstraints,
        audio: false
      });
      setStream(mediaStream);
      setIsWebcamActive(true);
      // The actual video element may not be mounted yet; attaching and readiness is handled in an effect below.
    } catch (err) {
      let type = 'generic';
      let message = err?.message || 'Unable to access camera.';
      if (err?.name === 'NotAllowedError') {
        type = 'permission';
        message = 'Camera permission denied. Please allow access and try again.';
      } else if (err?.name === 'NotFoundError') {
        type = 'device';
        message = 'No camera device found on this device.';
      } else if (err?.name === 'NotReadableError') {
        type = 'busy';
        message = 'Camera is in use by another application. Close it and try again.';
      } else if (err?.name === 'SecurityError') {
        type = 'security';
        message = 'Secure connection (HTTPS) is required for camera access.';
      } else if (err?.name === 'UnsupportedBrowser') {
        type = 'browser';
        message = 'Your browser does not support camera access. Use Chrome, Firefox, Edge, or Safari.';
      }
      setError({ message, type });
      setStarting(false);
      stopWebcam();
    }
  }, [starting, isWebcamActive, stopWebcam]);

  // Attach stream to video and mark ready once frames can play
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !stream) return;

    let cancelled = false;
    let fallbackTimer;

    const markReady = () => {
      if (cancelled) return;
      setIsVideoReady(true);
      setStarting(false);
      video.play().catch(() => {});
    };

    const onLoadedMetadata = () => {
      // Sometimes loadedmetadata fires before dimensions are non-zero; we still kick off play.
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        markReady();
      }
    };
    const onCanPlay = () => markReady();
    const onPlaying = () => markReady();
    const onError = () => {
      if (cancelled) return;
      setError({ message: 'An error occurred while initializing the camera.', type: 'generic' });
      setStarting(false);
    };

    // Attach stream and listeners
    try {
      video.srcObject = stream;
    } catch (_) {
      // Fallback for older browsers
      // eslint-disable-next-line no-undef
      if (typeof URL !== 'undefined') video.src = URL.createObjectURL(stream);
    }
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('playing', onPlaying);
    video.addEventListener('error', onError);

    // Fallback: poll for non-zero dimensions up to ~3 seconds
    const startTime = Date.now();
    const pollDimensions = () => {
      if (cancelled) return;
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        markReady();
        return;
      }
      if (Date.now() - startTime < 3000) {
        fallbackTimer = setTimeout(pollDimensions, 150);
      } else {
        setStarting(false);
        setError({ message: 'Camera is taking too long to initialize. Please try again.', type: 'timeout' });
      }
    };
    pollDimensions();

    return () => {
      cancelled = true;
      clearTimeout(fallbackTimer);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('error', onError);
    };
  }, [stream]);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) {
      setError({ message: 'Camera not ready. Please try again.', type: 'generic' });
      return;
    }
    const video = videoRef.current;
    if (!isVideoReady || video.videoWidth === 0 || video.videoHeight === 0) {
      setError({ message: 'Video not ready yet. Please wait a moment.', type: 'generic' });
      return;
    }
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    setIsCapturing(true);
    try {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const file = new File([blob], `patient-photo-${Date.now()}.jpg`, {
              type: PHOTO_CAPTURE.captureFormat,
              lastModified: Date.now()
            });
            onPhotoCapture?.(file);
            stopWebcam();
          } else {
            setError({ message: 'Failed to capture photo. Please try again.', type: 'generic' });
          }
          setIsCapturing(false);
        },
        PHOTO_CAPTURE.captureFormat,
        PHOTO_CAPTURE.captureQuality
      );
    } catch (e) {
      setIsCapturing(false);
      setError({ message: 'Failed to capture photo. Please try again.', type: 'generic' });
    }
  }, [isVideoReady, onPhotoCapture, stopWebcam]);

  const handleFileUpload = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > PHOTO_CAPTURE.maxFileSize) {
        setError({ message: `File size must be less than ${PHOTO_CAPTURE.maxFileSize / (1024 * 1024)}MB`, type: 'file' });
        e.target.value = '';
        return;
      }
      if (!PHOTO_CAPTURE.allowedTypes.includes(file.type)) {
        setError({ message: 'Please select a valid image file (JPEG, PNG, or WebP).', type: 'file' });
        e.target.value = '';
        return;
      }
      setError(null);
      onPhotoCapture?.(file);
    },
    [onPhotoCapture]
  );

  const handleRemovePhoto = useCallback(() => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    setError(null);
    onPhotoRemove?.();
  }, [onPhotoRemove]);

  useEffect(() => {
    return () => {
      try {
        if (stream) stream.getTracks().forEach((t) => t.stop());
      } catch (_) {}
    };
  }, [stream]);

  return (
    <div className={`${className}`}>
      <div className="bg-white rounded-xl border-2 border-theme-border/30 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-theme-blue/30 rounded-lg flex items-center justify-center">
              <Camera className="w-5 h-5 text-theme-border" />
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-theme-heading">Patient Photo</h3>
            <p className="text-sm text-theme-text">Capture or upload patient photo</p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-theme-error/10 border border-theme-error/30 rounded-lg">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-theme-error mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-theme-error mb-1">Camera/File Error</h4>
                <p className="text-sm text-theme-error/80">{error.message}</p>
              </div>
            </div>
          </div>
        )}

        {capturedPhoto && (
          <div className="mb-6">
            <div className="relative inline-block">
              <img
                src={typeof capturedPhoto === 'string' ? capturedPhoto : URL.createObjectURL(capturedPhoto)}
                alt="Captured patient"
                className="w-32 h-32 object-cover rounded-lg border border-theme-border/30"
              />
              <button
                onClick={handleRemovePhoto}
                className="absolute -top-2 -right-2 w-6 h-6 bg-theme-error text-white rounded-full flex items-center justify-center hover:bg-theme-error/80 transition-colors"
                disabled={disabled || loading}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
            <p className="text-sm text-theme-text mt-2">Photo captured successfully</p>
          </div>
        )}

        {isWebcamActive && !capturedPhoto && (
          <div className="mb-6">
            <div className="relative bg-black rounded-lg overflow-hidden shadow border border-theme-border/30" style={{ width: '500px', height: '375px', margin: '0 auto' }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
                style={{ opacity: isVideoReady ? 1 : 0.5, transition: 'opacity 0.3s ease' }}
              />
              {!isVideoReady && (
                <div className="absolute inset-0 flex flex-col items-center justify-center space-y-3 bg-black/60 text-white">
                  <RefreshCw className="w-8 h-8 animate-spin" />
                  <p className="text-sm font-medium">Initializing camera...</p>
                </div>
              )}
              <div className="absolute top-3 left-3 bg-theme-error text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center space-x-2 shadow">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                <span>LIVE</span>
              </div>
            </div>

            <div className="flex justify-center gap-4 mt-6">
              <button onClick={capturePhoto} disabled={disabled || loading || isCapturing || !isVideoReady} className="px-4 py-2 bg-theme-border text-white font-medium rounded-lg hover:bg-theme-border/90 transition-colors disabled:opacity-50">
                <span className="inline-flex items-center"><Camera className="w-4 h-4 mr-2" /> {isVideoReady ? 'Capture Photo' : 'Preparing...'}</span>
              </button>
              <button onClick={stopWebcam} disabled={disabled || loading} className="px-4 py-2 border border-theme-border/50 rounded-lg text-theme-heading hover:bg-theme-blue/10 transition-colors">
                <span className="inline-flex items-center"><X className="w-4 h-4 mr-2" /> Cancel</span>
              </button>
            </div>
          </div>
        )}

        {!isWebcamActive && !capturedPhoto && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button onClick={startWebcam} disabled={disabled || loading || starting} className="px-4 py-2 bg-theme-border text-white font-medium rounded-lg hover:bg-theme-border/90 transition-colors disabled:opacity-50">
                <span className="inline-flex items-center"><Camera className="w-4 h-4 mr-2" /> {starting ? 'Starting…' : 'Use Webcam'}</span>
              </button>
              <button onClick={() => fileInputRef.current?.click()} disabled={disabled || loading} className="px-4 py-2 border border-theme-border/50 rounded-lg text-theme-heading hover:bg-theme-blue/10 transition-colors">
                <span className="inline-flex items-center"><Upload className="w-4 h-4 mr-2" /> Upload File</span>
              </button>
            </div>

            <input ref={fileInputRef} type="file" accept={PHOTO_CAPTURE.allowedTypes.join(',')} onChange={handleFileUpload} className="hidden" />

            <div className="text-center space-y-1">
              <p className="text-sm text-theme-text">Upload images up to {PHOTO_CAPTURE.maxFileSize / (1024 * 1024)}MB</p>
              <p className="text-xs text-theme-text/70">Supported formats: JPEG, PNG, WebP</p>
            </div>
          </div>
        )}

        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
};

export default WebcamCapture;
